import React, { useEffect, useState } from "react";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";
import { DataGrid } from "@mui/x-data-grid";
import { useDeleteSubadminMutation, useGetSubadminListQuery } from "../redux/features/user/userApi";
import { FaEdit, FaSearch, FaPlus, FaUserShield } from "react-icons/fa";
import { MdDelete, MdOutlinePerson } from "react-icons/md";
import Navbar from "../Components/Navbar";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Subadmin = () => {
  const { data, isSuccess, isError, isLoading } = useGetSubadminListQuery();
  const [deleteSubadmin, { isSuccess: deleteSuccess, data: deleteData, isError: deleteError, isLoading: deleteLoading }] = useDeleteSubadminMutation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);

  useEffect(() => {
    if (deleteSuccess && deleteData.status == true) {
      toast.success(deleteData.message)
    }
  }, [deleteSuccess])

  useEffect(() => {
    const rows = [];
    data?.data &&
      data?.data.forEach((item, index) => {
        const options = {
          year: "numeric",
          month: "long",
          day: "numeric",
        };

        const date = new Date(item.created_at).toLocaleDateString(
          "en-US",
          options
        );
        const active = item.isActive ? "Active" : "Deactive";

        rows.push({
          id: index + 1,
          userId: item._id,
          email: item.email,
          name: item.username,
          created_at: date,
          status: active,
        });
      });

    if (searchTerm) {
      const filtered = rows.filter(row =>
        row.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRows(filtered);
    } else {
      setFilteredRows(rows);
    }
  }, [data, searchTerm]);

  const columns = [
    { 
      field: "id", 
      headerName: "S.No", 
      flex: 0.25, 
      minWidth: 80,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => (
        <span className="font-semibold text-gray-700">{params.value}</span>
      )
    },
    { 
      field: "email", 
      headerName: "Email", 
      flex: 1, 
      minWidth: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => (
        <span className="text-gray-600">{params.value}</span>
      )
    },
    { 
      field: "name", 
      headerName: "User Name", 
      flex: 0.5, 
      minWidth: 150,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
            {params.value?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <span className="font-medium text-gray-800">{params.value}</span>
        </div>
      )
    },
    { 
      field: "created_at", 
      headerName: "Created On", 
      flex: 0.75, 
      minWidth: 180,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => (
        <span className="text-gray-600">{params.value}</span>
      )
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 120,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        const isActive = params.value === "Active";
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            isActive 
              ? "bg-green-100 text-green-700 border border-green-300" 
              : "bg-yellow-100 text-yellow-700 border border-yellow-300"
          }`}>
            {params.value}
          </span>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.25,
      minWidth: 100,
      headerClassName: "super-app-theme--header",
      sortable: false,
      renderCell: (params) => {
        return (
          <button
            onClick={() => {
              const id = params.row.userId;
              navigate(`/subadmin/${id}`);
            }}
            className="p-2 rounded-lg hover:bg-blue-100 transition-all duration-200 group"
            title="Edit"
          >
            <FaEdit className="text-blue-600 group-hover:text-blue-800 transition-colors" size={18} />
          </button>
        );
      },
    },
    {
      field: "delete",
      headerName: "Delete",
      flex: 0.25,
      minWidth: 100,
      headerClassName: "super-app-theme--header",
      sortable: false,
      renderCell: (params) => {
        return (
          <button
            onClick={async () => {
              const id = params.row.userId;
              if (window.confirm("Are you sure you want to delete this subadmin?")) {
                await deleteSubadmin(id);
              }
            }}
            className="p-2 rounded-lg hover:bg-red-100 transition-all duration-200 group"
            title="Delete"
          >
            <MdDelete className="text-red-500 group-hover:text-red-700 transition-colors" size={20} />
          </button>
        );
      },
    },
  ];

  const style = {
    height: 100,
    width: 100,
    margin: "auto",
    alignItem: "center",
  };

  return isLoading ? (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <Lottie animationData={loader} style={style} loop={true} width={20} />
    </div>
  ) : (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 pt-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto py-8">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <FaUserShield className="text-white text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Subadmin Management
                  </h1>
                  <p className="text-gray-500 text-sm mt-1">Manage your subadmin accounts</p>
                </div>
              </div>
              <button
                onClick={() => navigate("add-new")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                <FaPlus className="text-sm" />
                <span>Add New Subadmin</span>
              </button>
            </div>

            {/* Search Bar */}
            <div className="mt-6 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 outline-none text-gray-700 placeholder-gray-400"
              />
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                    <MdOutlinePerson className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Total Subadmins</p>
                    <p className="text-2xl font-bold text-blue-800">{filteredRows.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                    <FaUserShield className="text-white" size={18} />
                  </div>
                  <div>
                    <p className="text-green-600 text-sm font-medium">Active</p>
                    <p className="text-2xl font-bold text-green-800">
                      {filteredRows.filter(row => row.status === "Active").length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500 flex items-center justify-center">
                    <MdOutlinePerson className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="text-yellow-600 text-sm font-medium">Inactive</p>
                    <p className="text-2xl font-bold text-yellow-800">
                      {filteredRows.filter(row => row.status === "Deactive").length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Grid Section */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-800">Subadmin List</h2>
            </div>
            <div className="p-4">
              <DataGrid
                columns={columns}
                rows={filteredRows}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                autoHeight
                sx={{
                  "& .MuiDataGrid-root": {
                    border: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #f3f4f6",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f8fafc",
                    borderBottom: "2px solid #e5e7eb",
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 600,
                      color: "#374151",
                    },
                  },
                  "& .MuiDataGrid-row": {
                    "&:hover": {
                      backgroundColor: "#f0f9ff",
                    },
                  },
                  "& .MuiPagination-root": {
                    marginTop: "16px",
                  },
                }}
              />
            </div>
          </div>

          {/* Empty State */}
          {filteredRows.length === 0 && !isLoading && (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <MdOutlinePerson className="text-gray-400" size={40} />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Subadmins Found</h3>
              <p className="text-gray-500 mb-6">Get started by adding your first subadmin</p>
              <button
                onClick={() => navigate("add-new")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                <FaPlus className="text-sm" />
                <span>Add Subadmin</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Subadmin;
